import React, { useState } from 'react';
import { Box, Button, Dialog, TextField, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import JsonImportDiffView from './JsonImportDiffView';
import type { SubPatternOverrideCandidate } from './SubPatternEditModal';
import type { SubPatternType } from '../../../types/subPattern';
import type { ItemListType } from '../../../types/common';
import { buildItemListExport } from '../../../utils/pattern/itemListJsonExport';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  parseSubPatternJson,
  resolveOverrideItemNames,
  stringifySubPatternJson,
  type SubPatternJsonType,
} from '../../../utils/pattern/subPatternJson';
import {
  validateSubPatternJson,
  subPatternJsonErrorMessage,
} from '../../../utils/pattern/subPatternJsonValidation';
import { subPatternJsonLabel } from '../../../constants/subPattern';
import { boldMd, regularXl, regularSm, regularMd } from '../../../constants/location';
import {
  gray5,
  gray20,
  gray30,
  gray60,
  gray80,
  gray100,
  orange50,
  red50,
  blue50,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';

/**
 * 子パターンのJSON入力モーダル（#3372）。
 *
 * 単体（編集モーダルから）と一括（パターン一覧から）の両方で使う。
 * どちらも「JSONを貼る → 差分を確認 → 反映/登録」の2段階にする。
 *
 * JSONには親パターンの項目名を持たないため、
 * 対象項目の一覧をここで現在の item_list から引いて見せる（§4.5）。
 * これが取り込み前の精査の材料になる。
 */

const styles = {
  /**
   * 親パターンの項目リストJSON（ItemListManager）と同じ体裁にそろえる。
   * 差分表示はJSONの行をそのまま並べるため、入力時より広い幅を確保する
   */
  dialog: (isPreview: boolean) => ({
    '& .MuiPaper-root': {
      borderRadius: '8px',
      padding: '24px',
      // 参照用の2つを左右に並べるため、入力時も広めに取る（#3372）
      width: isPreview ? '1100px' : '1000px',
      maxWidth: '95vw',
      // 記入例・対象項目・差分と縦に長くなるため、画面内に収めてスクロールさせる
      maxHeight: 'calc(100vh - 32px)',
    },
  }),
  title: {
    marginBottom: '8px',
  },
  lead: {
    color: gray60,
    marginBottom: '16px',
  },
  /**
   * 参照用の2つ（親パターンの設定JSON・記入例）を左右に並べる（#3372）。
   * 縦に積むと入力欄が画面外へ押し出され、例を見ながら貼れなくなる
   */
  referenceRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    // 幅が狭いときは縦に折り返す
    '@media screen and (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  // 2つを同じ幅にする（minWidth: 0 がないと中身の長い行で片方が広がる）
  referenceColumn: {
    flex: 1,
    minWidth: 0,
  },
  referenceBox: {
    backgroundColor: gray5,
    border: `1px solid ${gray20}`,
    borderRadius: '4px',
    padding: '12px',
    // 左右に並べたぶん縦を抑え、入力欄を画面内に残す
    height: '220px',
    overflow: 'auto',
    whiteSpace: 'pre',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: gray80,
    cursor: 'pointer',
    '&:hover': {
      borderColor: gray100,
    },
  },
  /**
   * JSONの入力欄。
   *
   * #3153 で palette.primary をブランドのオレンジにしたため、
   * 色を明示しない TextField はフォーカス・ホバーで枠がオレンジになる。
   * ここはブランド色を主張する場所ではないので、親パターンの項目リストJSON
   * （ItemListManager）と同じく枠を黒に戻す。
   *
   * 縦にドラッグで広げられるようにするのも同じ理由（項目数が多いJSONを編集するため）。
   */
  input: {
    fontFamily: 'monospace',
    '& .MuiInputBase-inputMultiline': {
      resize: 'vertical',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
    },
  },
  /** 参照用ブロックの見出し行。右端にコピーの導線を置く（#3372） */
  referenceHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
    minHeight: '20px',
  },
  /** 参照ブロックの見出し。薄いと読みにくいため本文と同じ濃さにする */
  referenceHeading: {
    ...regularSm,
    color: gray100,
  },
  copyAction: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    gap: '4px',
  },
  copyActionIcon: {
    color: orange50,
    fontSize: '14px',
  },
  copyActionText: {
    color: orange50,
    fontSize: '12px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  targetList: {
    border: `1px solid ${gray30}`,
    borderRadius: '4px',
    padding: '12px',
    marginBottom: '16px',
    maxHeight: '200px',
    overflow: 'auto',
  },
  targetRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'baseline',
    padding: '2px 0',
  },
  targetItemId: {
    fontFamily: 'monospace',
    color: gray60,
    minWidth: '56px',
  },
  summaryRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
  },
  error: {
    color: red50,
    marginTop: '8px',
  },
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '10px 12px',
    marginBottom: '12px',
    borderRadius: '4px',
    border: `1px solid ${red50}`,
    color: red50,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  actionButton: {
    minWidth: '120px',
    height: '40px',
  },
} as const;

/**
 * 一括登録の記入例。snake_case で示す（#3195 と同じ方針）。
 *
 * 複数件は `[ ]` で囲んで「,」で区切る。
 * 上書きできる4種（取得ルール・この帳票での項目名・固定値・AI読取のON/OFF）が
 * ひととおり出るように組んである。
 */
const jsonExampleBulk = `[
  {
    "sub_pattern_name": "A社フォーマット",
    "description": "発注元が「株式会社サンプル」と印字されている注文書",
    "item_overrides": [
      {
        "item_id": "2",
        "item_name": "発注元",
        "extraction_rule": "右上の「発注元」欄に印字された会社名を取得する"
      },
      {
        "item_id": "5",
        "fixed_value": "営業部",
        "is_ocr_target": false
      }
    ]
  },
  {
    "sub_pattern_name": "B社フォーマット",
    "description": "左上に「御注文書」と印字され、明細が2段組になっている注文書",
    "item_overrides": [
      {
        "item_id": "2",
        "extraction_rule": "左上の「宛先」欄に印字された会社名を取得する"
      },
      {
        "item_id": "7-2",
        "extraction_rule": "「数量」列の値を取得する。単位（個・本）は含めない",
        "fixed_value": "0"
      }
    ]
  }
]`;

/**
 * 単体編集の記入例。
 *
 * この画面では1件しか反映できないため、配列にしない形で示す（#3372）。
 * 配列を貼られた場合はエラーで弾く
 */
const jsonExampleSingle = `{
  "sub_pattern_name": "A社フォーマット",
  "description": "発注元が「株式会社サンプル」と印字されている注文書",
  "item_overrides": [
    {
      "item_id": "2",
      "item_name": "発注元",
      "extraction_rule": "右上の「発注元」欄に印字された会社名を取得する"
    },
    {
      "item_id": "5",
      "fixed_value": "営業部",
      "is_ocr_target": false
    }
  ]
}`;

/** 反映・登録の内訳。一括取り込みのサマリ表示に使う */
export type SubPatternJsonApplySummary = {
  items: SubPatternJsonType[];
  /** 新規作成になる件数（一括のみ） */
  createCount: number;
  /** 更新になる件数（一括のみ） */
  updateCount: number;
};

interface SubPatternJsonModalProps {
  open: boolean;
  /** 一括取り込みか。false なら単体（編集モーダルのフォームへ反映する） */
  isBulk: boolean;
  /** 親パターンから組み立てた上書き候補。検証と項目名の表示に使う */
  candidates: SubPatternOverrideCandidate[];
  /**
   * 親パターンの項目リスト（#3372）。
   * 参照用に親パターンの設定JSONを出すために使う（項目IDを調べる手段がこれしかない）
   */
  itemList: ItemListType[];
  /** 同一パターンの既存の子パターン */
  existingSubPatterns: SubPatternType[];
  /** 編集中の子パターンID（単体のみ。自分自身を名称の重複判定から除く） */
  editingSubPatternId?: string;
  /** 単体のとき、モーダルを開いた時点の内容をJSONで初期表示する */
  initialJson?: SubPatternJsonType | null;
  /** 処理中（一括登録の実行中）。ボタンを止める */
  submitting?: boolean;
  onClose: () => void;
  /** 検証を通ったJSONを渡す。単体は1件、一括は複数件 */
  onApply: (summary: SubPatternJsonApplySummary) => void;
}

const SubPatternJsonModal: React.FC<SubPatternJsonModalProps> = ({
  open,
  isBulk,
  candidates,
  itemList,
  existingSubPatterns,
  editingSubPatternId,
  initialJson,
  submitting = false,
  onClose,
  onApply,
}) => {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  // 検証を通したJSON。null のあいだは入力画面、入ると差分プレビューになる
  const [pending, setPending] = useState<SubPatternJsonType[] | null>(null);
  // 直近にコピーしたもの。ボタンの文言を一時的に変えて手応えを出す
  const [copiedTarget, setCopiedTarget] = useState<'parent' | 'example' | null>(null);

  // 開いた時点の内容で初期化する。単体では現在の設定をJSONで出し、
  // IDを保ったまま書き換えられるようにする（#3039 と同じ）
  React.useEffect(() => {
    if (!open) return;
    setText(initialJson ? stringifySubPatternJson(initialJson) : '');
    setError('');
    setPending(null);
    setCopiedTarget(null);
  }, [open, initialJson]);

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  /** 「差分を確認」。パース → 検証まで通してから差分プレビューへ進む */
  const handleValidate = (): void => {
    const parsed = parseSubPatternJson(text);
    if (!parsed) {
      setError(subPatternJsonErrorMessage.invalidFormat);
      return;
    }

    // 単体の編集では1件しか反映できない（#3372）。
    // 複数件を黙って1件目だけ採用すると、残りが消えたことに気づけないため弾く
    if (!isBulk && parsed.items.length > 1) {
      setError(subPatternJsonErrorMessage.singleOnly);
      return;
    }

    const validationError = validateSubPatternJson({
      items: parsed.items,
      raws: parsed.raws,
      candidates,
      existingSubPatterns,
      editingSubPatternId,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setPending(parsed.items);
  };

  /** 差分プレビューから戻る */
  const handleBack = (): void => {
    setPending(null);
    setError('');
  };

  /**
   * 既存と同名の件数（＝更新になる件数）。
   * 一括取り込みでは名称で突き合わせるため、サマリと onApply で同じ数え方を使う
   */
  const updateCount = React.useMemo(() => {
    if (!pending) return 0;
    const existingNames = new Set(
      existingSubPatterns.map(sub => (sub.sub_pattern_name ?? '').trim()),
    );
    return pending.filter(item => existingNames.has(item.sub_pattern_name)).length;
  }, [pending, existingSubPatterns]);

  const handleApply = (): void => {
    if (!pending) return;
    onApply({
      items: pending,
      createCount: pending.length - updateCount,
      updateCount,
    });
  };

  /**
   * 親パターンの設定JSON（#3372）。参照用に出す。
   * 親パターンの「JSONでコピー」と同じ関数を使うため、出力が食い違わない
   */
  const parentJson = React.useMemo(() => {
    const items = buildItemListExport(itemList);
    return items.length === 0 ? '' : JSON.stringify(items, null, 2);
  }, [itemList]);

  /**
   * 参照用のJSONをクリップボードへ。
   * 親パターンの設定JSONはIDを調べてAIへ渡す用途、記入例は書き出しの雛形にする用途。
   * どちらも「読んで写す」より「コピーして直す」ほうが速いため、両方コピー可にする
   */
  const handleReferenceCopy = async (
    target: 'parent' | 'example',
    value: string,
  ): Promise<void> => {
    if (value === '') return;
    try {
      await navigator.clipboard.writeText(value);
      setError('');
      setCopiedTarget(target);
    } catch {
      setError(subPatternJsonLabel.copyFailed);
    }
  };

  /** 差分プレビューの前後JSON。どちらも同じ関数を通して整形差を出さない */
  const beforeJson = initialJson ? stringifySubPatternJson(initialJson) : '';
  const afterJson = pending ? stringifySubPatternJson(isBulk ? pending : pending[0]) : '';

  /** 対象項目の一覧。JSONに項目名が無い代わりに現在の項目名を見せる */
  const targets = React.useMemo(() => {
    if (!pending) return [];
    return pending.flatMap(item =>
      resolveOverrideItemNames(item.item_overrides, candidates).map(resolved => ({
        subPatternName: item.sub_pattern_name,
        ...resolved,
      })),
    );
  }, [pending, candidates]);

  // 表示とコピーで同じ値を使う
  const jsonExample = isBulk ? jsonExampleBulk : jsonExampleSingle;
  const title = isBulk ? subPatternJsonLabel.bulkTitle : subPatternJsonLabel.editTitle;
  const lead = isBulk ? subPatternJsonLabel.bulkLead : subPatternJsonLabel.editLead;
  // 差分プレビュー中か。ダイアログの幅を広げるかの判定に使う
  const isPreview = pending !== null;

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog(isPreview)} maxWidth={false}>
      <Typography sx={{ ...regularXl, ...styles.title }}>{title}</Typography>
      <Typography sx={{ ...regularSm, ...styles.lead }}>{lead}</Typography>

      {pending === null ? (
        <>
          {/*
            親パターンの設定JSON（#3372）。
            JSONは item_id で項目を指すが、どの項目がどのIDなのかは画面のどこにも出ていない。
            対応表を自前で組むより、親パターンのJSONをそのまま出したほうが
            項目IDに加えて型・取得ルール・選択肢まで分かる（AIへ渡す材料にもなる）。
            クリックでまるごとコピーできるようにする
          */}
          {/*
            参照用の2つ（親パターンの設定JSON・記入例）は左右に並べる。
            縦に積むと入力欄が画面外へ押し出され、例を見ながら貼れなくなるため
          */}
          <Box sx={styles.referenceRow}>
            <Box sx={styles.referenceColumn}>
              <Box sx={styles.referenceHeader}>
                <Typography sx={styles.referenceHeading}>
                  {subPatternJsonLabel.parentJsonHeading}
                </Typography>
                {parentJson !== '' && (
                  <Box
                    sx={styles.copyAction}
                    onClick={(): void => void handleReferenceCopy('parent', parentJson)}
                  >
                    <ContentCopyOutlinedIcon sx={styles.copyActionIcon} />
                    <Typography sx={styles.copyActionText}>
                      {copiedTarget === 'parent'
                        ? subPatternJsonLabel.copyActionDone
                        : subPatternJsonLabel.copyAction}
                    </Typography>
                  </Box>
                )}
              </Box>
              {parentJson === '' ? (
                <Typography sx={{ ...regularSm, color: gray80 }}>
                  {subPatternJsonLabel.noItem}
                </Typography>
              ) : (
                <Box
                  sx={styles.referenceBox}
                  onClick={(): void => void handleReferenceCopy('parent', parentJson)}
                  title={subPatternJsonLabel.copyHint}
                >
                  {parentJson}
                </Box>
              )}
            </Box>

            <Box sx={styles.referenceColumn}>
              <Box sx={styles.referenceHeader}>
                <Typography sx={styles.referenceHeading}>
                  {isBulk
                    ? subPatternJsonLabel.exampleHeadingBulk
                    : subPatternJsonLabel.exampleHeadingSingle}
                </Typography>
                <Box
                  sx={styles.copyAction}
                  onClick={(): void => void handleReferenceCopy('example', jsonExample)}
                >
                  <ContentCopyOutlinedIcon sx={styles.copyActionIcon} />
                  <Typography sx={styles.copyActionText}>
                    {copiedTarget === 'example'
                      ? subPatternJsonLabel.copyActionDone
                      : subPatternJsonLabel.copyAction}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={styles.referenceBox}
                onClick={(): void => void handleReferenceCopy('example', jsonExample)}
                title={subPatternJsonLabel.copyHint}
              >
                {jsonExample}
              </Box>
            </Box>
          </Box>
          <TextField
            multiline
            rows={14}
            fullWidth
            value={text}
            onChange={(e): void => setText(e.target.value)}
            placeholder={subPatternJsonLabel.placeholder}
            sx={styles.input}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '12px' } }}
          />
        </>
      ) : (
        <>
          {isBulk && (
            <Box sx={styles.summaryRow}>
              <Typography sx={{ ...regularMd, color: blue50 }}>
                {subPatternJsonLabel.summary(pending.length - updateCount, updateCount)}
              </Typography>
            </Box>
          )}

          {/* 全部書きのため、JSONに無い上書きは消える。件数ではなく仕組みを先に伝える */}
          {!isBulk && (
            <Box sx={styles.warning}>
              <WarningAmberRoundedIcon fontSize="small" />
              <Typography sx={regularSm}>{subPatternJsonLabel.replaceWarning}</Typography>
            </Box>
          )}

          <Typography sx={{ ...styles.referenceHeading, marginBottom: '4px' }}>
            {subPatternJsonLabel.targetHeading}
          </Typography>
          <Box sx={styles.targetList}>
            {targets.map((target, index) => (
              <Box key={`${target.subPatternName}-${target.itemId}-${index}`} sx={styles.targetRow}>
                <Typography sx={{ ...regularSm, ...styles.targetItemId }}>
                  {target.itemId}
                </Typography>
                <Typography sx={{ ...regularSm, color: target.itemName === null ? red50 : gray80 }}>
                  {isBulk ? `[${target.subPatternName}] ` : ''}
                  {target.itemName ?? subPatternJsonLabel.unknownItem}
                </Typography>
              </Box>
            ))}
          </Box>

          <JsonImportDiffView beforeJson={beforeJson} afterJson={afterJson} diffEntries={[]} />
        </>
      )}

      {error !== '' && <Typography sx={{ ...regularSm, ...styles.error }}>{error}</Typography>}

      <Box sx={styles.actions}>
        <Button
          onClick={pending === null ? handleClose : handleBack}
          disabled={submitting}
          sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
        >
          <Typography sx={{ ...boldMd, color: gray80 }}>
            {pending === null ? 'キャンセル' : '戻る'}
          </Typography>
        </Button>
        <Button
          onClick={pending === null ? handleValidate : handleApply}
          disabled={submitting}
          sx={[primaryButtonBackgroundColor, styles.actionButton]}
        >
          <Typography sx={{ ...boldMd, color: 'white' }}>
            {pending === null
              ? '差分を確認'
              : submitting
                ? '登録中...'
                : isBulk
                  ? subPatternJsonLabel.bulkApply
                  : subPatternJsonLabel.editApply}
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default SubPatternJsonModal;
